import { useContext, useEffect, useState } from 'react';
import { Box, Stack, Divider, Button, Tooltip, styled, useTheme, CircularProgress, alpha, lighten, useMediaQuery } from '@mui/material';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import { SidebarContext } from 'src/contexts/SidebarContext';
import HeaderButtons from './Buttons';
import HeaderUserbox from './Userbox';
import HeaderMenu from './Menu';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useUserContext } from 'src/components/ProtectedRoute/userProvider';
import { getUser, createStripeAccount } from 'src/apiCalls';
import { Link as RouterLink } from 'react-router-dom';
import logger from 'src/logger';
import Logo from 'src/components/LogoSign';

const HeaderWrapper = styled(Box)(
  ({ theme }) => `
        height: ${theme.header.height};
        color: ${theme.header.textColor};
        padding: ${theme.spacing(0, 2)};
        right: 0;
        z-index: 6;
        background-color: ${alpha(theme.header.background, 0.95)};
        backdrop-filter: blur(3px);
        position: fixed;
        justify-content: space-between;
        width: 100%;
        @media (min-width: ${theme.breakpoints.values.lg}px) {
            left: ${theme.sidebar.width};
            width: auto;
        }
        display: flex;
        overflow: visible;
        box-sizing: border-box;
        
        @media (max-width: ${theme.breakpoints.values.lg}px) {
            padding-right: ${theme.spacing(2)}; // Reduced padding
            left: 0;
        }
        
        // Ensure content doesn't overflow
        & > div {
            flex-shrink: 1;
            min-width: 0;
        }
`
);

function Header() {
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useUserContext();

  const [stripeOnboardingComplete, setStripeOnboardingComplete] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      if (user?.accessToken && user?.uid) {
        try {
          const userData = await getUser(user.accessToken, user.uid);
          
          // Handle case where userData might be null
          if (userData) {
            setStripeOnboardingComplete(userData.stripeOnboardingComplete);
          } else {
            // Handle case where user data doesn't exist yet
            setStripeOnboardingComplete(false);
          }
        } catch (error) {
          logger.error('Error fetching user data:', error);
          setStripeOnboardingComplete(false);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
  
    fetchUserData();
  }, [user]);



  if (loading) {
    return <CircularProgress />;
  }

  return (
    <HeaderWrapper
      display="flex"
      alignItems="center"
      sx={{
        boxShadow:
          theme.palette.mode === 'dark'
            ? `0 1px 0 ${alpha(
              lighten(theme.colors.primary.main, 0.7),
              0.15
            )}, 0px 2px 8px -3px rgba(0, 0, 0, 0.2), 0px 5px 22px -4px rgba(0, 0, 0, .1)`
            : `0px 2px 8px -3px ${alpha(
              theme.colors.alpha.black[100],
              0.2
            )}, 0px 5px 22px -4px ${alpha(
              theme.colors.alpha.black[100],
              0.1
            )}`
      }}
    >
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        alignItems="center"
        spacing={1}
      >
        <Box
          sx={{
            display: { xs: 'block', lg: 'none' },
            mr: 2
          }}
        >
          <Logo size="small" onClick={toggleSidebar} />
        </Box>
        <HeaderMenu />
      </Stack>
      <Box
        display="flex"
        alignItems="center"
        sx={{
          gap: 1,
          '& > *': { flexShrink: 0 } // Prevent children from shrinking
        }}
      >
        {user && !stripeOnboardingComplete && (
          <Button
            variant="contained"
            color="secondary"
            component={RouterLink} // Use RouterLink for navigation
            to="/stripe/setup" // Replace with the route to your intermediate page
            startIcon={isMobile ? null : <AccountBalanceWalletIcon />}
            sx={{
              minWidth: 0,
              px: isMobile ? 1 : 1.5,
              py: 0.5,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              '& .MuiButton-startIcon': {
                mr: isMobile ? 0.5 : 1,
              },
            }}
          >
            {isMobile ? 'Setup Payments' : 'Set Up Payment Account'}
          </Button>
        )}

        <HeaderButtons />
        <HeaderUserbox />

        <Box
          component="span"
          sx={{
            display: { lg: 'none', xs: 'inline-block' },
            position: 'relative',
            zIndex: 7,
          }}
        >
          <Tooltip arrow title="Toggle Menu">
            <Button
              color="primary"
              onClick={toggleSidebar}
              sx={{
                minWidth: 0,
                p: 1
              }}
            >
              {!sidebarToggle ? (
                <MenuTwoToneIcon fontSize="small" />
              ) : (
                <CloseTwoToneIcon fontSize="small" />
              )}
            </Button>
          </Tooltip>
        </Box>
      </Box>
    </HeaderWrapper>
  );
}

export default Header;